import { useAuth } from '@polvo-labs/react-auth'
import { formatRelative } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { RoomsContext } from '../../../context/rooms-context'
import Room from '../../../models/Room'
import isCommon from '../../../utils/is-common'
import Button from '../../Button'
import Icon from '../../Icon'
import Loader from '../../Loader'
import Text from '../../Text'
import { mapToComponent, Message } from '../type'
import { Baloon, Container, MessageContainer, NoMessages } from './styles'

interface Props {
  room?: Room
  loadingMessages: boolean
  messages: Message[]
  loadingMoreMessages: boolean
  hasMoreMessages: boolean
  loadMoreMessages: () => Promise<void>
  visualizing: boolean
  setIsArchivedMessages?: (archived: boolean) => void
  isArchivedMessages?: boolean
}

const Messages: React.FC<Props> = ({
  room,
  loadingMessages,
  messages,
  loadingMoreMessages,
  hasMoreMessages,
  loadMoreMessages,
  visualizing,
  setIsArchivedMessages,
  isArchivedMessages
}) => {
  const { user } = useAuth()
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const { roomsState } = useContext(RoomsContext)

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef && messagesContainerRef.current) {
      const div = messagesContainerRef.current

      div.scrollTop = div.scrollHeight
    }
  }, [messagesContainerRef])

  const scrollToPosition = useCallback(height => {
    if (messagesContainerRef && messagesContainerRef.current) {
      const div = messagesContainerRef.current

      div.scrollTop = messagesContainerRef.current?.scrollHeight - height
    }
  }, [messagesContainerRef])

  useEffect(() => {

    scrollToBottom()

  }, [roomsState.visualizing, scrollToBottom])

  const handleScroll = (e: any) => {
    let element = e.target
    if (element.scrollTop === 0) {
      const height = messagesContainerRef.current?.scrollHeight
      loadMoreMessages().then(() => {
        scrollToPosition(height)
      })
    }
  }

  return (
    <Container
      onScroll={handleScroll}
      ref={messagesContainerRef}
    >
      {loadingMoreMessages && (
        <Icon
          name="dark-loader"
        />
      )}
      {loadingMessages ? (
        <Loader
          darkMode
        />
      ) : messages && messages.length ? (
        <>
          {setIsArchivedMessages && (
            <Button
              label={!isArchivedMessages ? "Carregar mensagens arquivadas" : "Ocultar mensagens arquivadas"}
              block
              backgroundColor="transparent"
              fontColor="gray"
              disabled={loadingMoreMessages}
              onClick={() => {
                setIsArchivedMessages(!isArchivedMessages)
              }}
            />
          )}
          {hasMoreMessages && !loadingMoreMessages && (
            <Button
              label="Carregar mais mensagens"
              block
              backgroundColor="transparent"
              fontColor="gray"
              disabled={loadingMoreMessages}

              onClick={() => {
                const height = messagesContainerRef.current?.scrollHeight
                loadMoreMessages().then(() => {
                  scrollToPosition(height)
                })
              }}
            />
          )}
          {[...messages].reverse().map((m, i, arr) => {
            const Component = mapToComponent[m.type] || Text
            const isFromOperator = m.user?.id !== room?.user.id

            const type = m.user?.id === user.id ||
              (isFromOperator && !isCommon(user)) ? 'sent' : 'received'

            const isFirstSentFromGroup = i === 0 || (
              arr[i - 1].user!.id !== m.user!.id &&
              (
                (isFromOperator && isCommon(arr[i - 1].user!)) ||
                !isFromOperator
              )
            )

            const isLastSentFromGroup = i === arr.length - 1 || (
              arr[i + 1].user!.id !== m.user!.id &&
              (
                (isFromOperator && isCommon(arr[i + 1].user!)) ||
                !isFromOperator
              )
            )

            const showOperatorName = isFromOperator && (
              i === 0 || arr[i - 1].user?.id !== m.user?.id
            )

            return (
              <MessageContainer
                key={m.id}
                type={type}
                data-id={m.id}
              >
                {showOperatorName && (
                  <Text
                    tag="span"
                    size="hint"
                  >
                    {m.user?.name}
                  </Text>
                )}
                <Baloon
                  style={{ transform: 'translate(0px, 0px) rotateZ(0deg) scaleY(1)', opacity: '1' }}
                  read={m.is_read}
                  isFirstSentFromUser={isFirstSentFromGroup}
                  isLastSentFromUser={isLastSentFromGroup}
                  type={type}
                  full={m.type === 'AUDIO'}
                >
                  <Component
                    message={m}
                    type={type}
                  />
                </Baloon>
                {isLastSentFromGroup && (
                  <Text
                    tag="span"
                    size="hint"
                  >
                    {formatRelative(new Date(m.created_at!), new Date(), {
                      locale: ptBR,
                    })}
                    {type === 'sent' && (
                      <>
                        {' '}-{' '}
                        {m.is_read ? 'Lida' : 'Não lida'}
                      </>
                    )}
                  </Text>
                )}
              </MessageContainer>
            )
          })}
        </>
      ) : (
            <NoMessages>
              <img
                alt="Placeholder sem mensagens"
                src="/assets/chat-placeholder.png"
              />
              <span>
                Nenhuma mensagem encontrada
          </span>
            </NoMessages>
          )}
    </Container>
  )
}

export default Messages
